'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Star, ChevronRight, MapPin, Compass } from 'lucide-react';
import type { ExcursionItem } from '@/app/esperienze/_views/shared';
import { formatEUR } from '@/components/home/catalog-variants/shared';
import 'leaflet/dist/leaflet.css';

interface ExperienceMapProps {
  items: ExcursionItem[];
  className?: string;
}

const SARDINIA_CENTER: [number, number] = [40.1209, 9.0129];
const DEFAULT_ZOOM = 8;

// Fallback porti (quando meeting_lat/lng mancano → match sul meeting_point).
const PORT_COORDINATES: Record<string, [number, number]> = {
  'Portisco': [40.9921, 9.4961], 'Olbia': [40.9226, 9.5068], 'Cagliari': [39.2238, 9.1217],
  'Alghero': [40.5589, 8.3190], 'Porto Torres': [40.8375, 8.4017], 'Arbatax': [39.9339, 9.7064],
  'La Maddalena': [41.2130, 9.4060], 'Castelsardo': [40.9133, 8.7133], 'Porto Cervo': [41.1313, 9.5308],
  'Golfo Aranci': [40.9836, 9.6225], 'Santa Teresa Gallura': [41.2416, 9.1878], 'Palau': [41.1789, 9.3822],
  'Cannigione': [41.0889, 9.4633], 'Bosa': [40.2983, 8.4989], 'Oristano': [39.9062, 8.5897],
  'Villasimius': [39.1408, 9.5181], 'Pula': [38.9208, 8.9736], 'Carloforte': [39.1467, 8.3089],
  'San Paolo': [40.8956, 9.6389], 'Cala Gonone': [40.2847, 9.6256],
};

const priceUnit = (mode?: string): string =>
  mode === 'per_person' || mode === 'tiered' ? '/pp' : mode === 'per_group' ? '/gruppo' : '';

const getCoords = (ex: ExcursionItem): [number, number] | null => {
  if (ex.meeting_lat != null && ex.meeting_lng != null) return [ex.meeting_lat, ex.meeting_lng];
  const hay = (ex.meeting_point || '').toLowerCase();
  if (hay) {
    for (const [key, coords] of Object.entries(PORT_COORDINATES)) {
      if (hay.includes(key.toLowerCase())) {
        const offset = () => (Math.random() - 0.5) * 0.02;
        return [coords[0] + offset(), coords[1] + offset()];
      }
    }
  }
  return null; // niente coord né match → escluso dalla mappa
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MapBits = { L: any; MapContainer: any; TileLayer: any; Marker: any; Popup: any };

const ExperienceMapInner: React.FC<ExperienceMapProps & MapBits> = ({ items, className = '', L, MapContainer, TileLayer, Marker, Popup }) => {
  const icon = L.divIcon({
    className: 'experience-marker',
    html: `
      <div style="width:34px;height:34px;background:linear-gradient(135deg,#06b6d4,#0e7490);border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(8,145,178,0.45);border:3px solid white;">
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="transform:rotate(45deg)"><circle cx="12" cy="10" r="3"/><path d="M12 2a8 8 0 0 0-8 8c0 5.4 8 12 8 12s8-6.6 8-12a8 8 0 0 0-8-8z"/></svg>
      </div>`,
    iconSize: [34, 34],
    iconAnchor: [17, 34],
    popupAnchor: [0, -32],
  });

  const withCoords = items
    .map((ex) => ({ ex, coords: getCoords(ex) }))
    .filter((it): it is { ex: ExcursionItem; coords: [number, number] } => it.coords !== null);

  const bounds: [[number, number], [number, number]] | null = (() => {
    if (withCoords.length === 0) return null;
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    for (const { coords } of withCoords) {
      const [lat, lng] = coords;
      minLat = Math.min(minLat, lat); maxLat = Math.max(maxLat, lat);
      minLng = Math.min(minLng, lng); maxLng = Math.max(maxLng, lng);
    }
    const minSpread = 0.15;
    if (maxLat - minLat < minSpread) { maxLat += minSpread / 2; minLat -= minSpread / 2; }
    if (maxLng - minLng < minSpread) { maxLng += minSpread / 2; minLng -= minSpread / 2; }
    return [[minLat, minLng], [maxLat, maxLng]];
  })();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapProps: any = bounds ? { bounds, boundsOptions: { padding: [50, 50] } } : { center: SARDINIA_CENTER, zoom: DEFAULT_ZOOM };

  return (
    <MapContainer {...mapProps} className={`w-full h-full rounded-2xl ${className}`} style={{ minHeight: '60vh' }} data-cid="esperienze-catalogo-map">
      <TileLayer attribution='&copy; OSM &copy; CARTO' url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" />
      <TileLayer url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png" opacity={0.8} />
      {withCoords.map(({ ex, coords }) => {
        const price = Number(ex.price) || 0;
        return (
          <Marker key={ex.id} position={coords} icon={icon}>
            <Popup minWidth={250} maxWidth={300}>
              <div className="p-0">
                <div className="relative h-28 -mx-4 -mt-4 mb-2.5 overflow-hidden rounded-t-lg bg-slate-100">
                  {ex.hero_image ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={ex.hero_image} alt={ex.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300"><Compass className="w-9 h-9" strokeWidth={1.2} /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  {ex.rating != null && (ex.reviews_count ?? 0) > 0 && (
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/90 px-2 py-0.5 rounded-md">
                      <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                      <span className="text-xs font-bold text-slate-800">{Number(ex.rating).toFixed(1)}</span>
                    </div>
                  )}
                </div>
                <div className="space-y-1.5">
                  <h3 className="text-sm font-bold text-slate-900 leading-tight">{ex.title}</h3>
                  {ex.meeting_point && (
                    <div className="flex items-center gap-1.5 text-slate-500 text-xs"><MapPin className="w-3 h-3 text-cyan-600" /><span>{ex.meeting_point}</span></div>
                  )}
                  <div className="flex items-center justify-between pt-1.5 border-t border-slate-100">
                    {price > 0 ? (
                      <div><span className="text-base font-bold text-slate-900">{formatEUR(price)}</span><span className="text-[10px] text-slate-400 font-semibold">{priceUnit(ex.pricing_mode)}</span></div>
                    ) : <span className="text-sm font-semibold text-slate-700">Su richiesta</span>}
                  </div>
                  <Link href={`/esperienze/${ex.slug}`} className="flex items-center justify-center gap-1.5 w-full py-1.5 bg-cyan-600 text-white rounded-lg text-xs font-bold hover:bg-cyan-700 transition-colors no-underline">
                    Vedi esperienza <ChevronRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

const ExperienceMap: React.FC<ExperienceMapProps> = (props) => {
  const [bits, setBits] = useState<MapBits | null>(null);
  useEffect(() => {
    Promise.all([import('leaflet'), import('react-leaflet')]).then(([L, RL]) => {
      setBits({ L: L.default, MapContainer: RL.MapContainer, TileLayer: RL.TileLayer, Marker: RL.Marker, Popup: RL.Popup });
    });
  }, []);
  if (!bits) {
    return (
      <div className={`w-full bg-slate-100 rounded-2xl flex items-center justify-center ${props.className}`} style={{ minHeight: '60vh' }} data-cid="esperienze-catalogo-map-loading">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-slate-500 text-sm font-medium">Caricamento mappa…</p>
        </div>
      </div>
    );
  }
  return <ExperienceMapInner {...props} {...bits} />;
};

export default ExperienceMap;
